import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user,     setUser]     = useState(null);
  const [customer, setCustomer] = useState(null);
  const [kids,     setKids]     = useState([]);
  const [loading,  setLoading]  = useState(true);

  const loadProfile = async (authUser) => {
    if (!authUser) {
      setCustomer(null);
      setKids([]);
      return;
    }
    try {
      const { data: custData } = await supabase
        .from('customers')
        .select('*')
        .eq('auth_id', authUser.id)
        .limit(1);

      if (custData?.[0]) {
        // Set customer immediately so dashboard can start rendering
        setCustomer(custData[0]);
        // Kids fetch runs without blocking
        supabase
          .from('kids')
          .select('*')
          .eq('customer_id', custData[0].id)
          .order('created_at', { ascending: true })
          .then(({ data: kidsData }) => setKids(kidsData || []))
          .catch(() => {});
      }
    } catch (e) {
      console.error('[AuthContext] loadProfile error:', e);
    }
  };

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!mounted) return;
        const u = session?.user || null;
        setUser(u);
        await loadProfile(u);
      } catch (e) {
        console.error('[AuthContext] getSession error:', e);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    init();

    // Fallback timeout — if getSession hangs, stop loading after 5 seconds
    const timeout = setTimeout(() => {
      if (mounted) setLoading(false);
    }, 5000);

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        const u = session?.user || null;
        setUser(u);
        await loadProfile(u);
      }
    );

    return () => {
      mounted = false;
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, []); // eslint-disable-line

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    setUser(data.user);
    await loadProfile(data.user);
    return data;
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setCustomer(null);
    setKids([]);
  };

  const value = {
    user,
    customer,
    kids,
    loading,
    signIn,
    signOut,
    refreshProfile: () => { try { return loadProfile(user); } catch (e) { console.error('[AuthContext] refreshProfile error:', e); } },
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
